<template>
  <div class="sr-wrapper">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#FF6907" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color:#fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color:#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- ══════════════ HERO ══════════════ -->
    <div class="sr-hero">
      <div class="sr-hero__top">
        <div class="sr-hero__left">
          <div class="sr-hero__eyebrow">
            <v-icon size="11" color="rgba(255,255,255,0.5)" class="mr-1">mdi-shield-check</v-icon>
            SARPA · Snake Operations
          </div>
          <h1 class="sr-hero__title">Snake Report <span class="sr-hero__accent">Details</span></h1>
        </div>
        <div class="sr-hero__right">
          <span :class="['sr-status-badge', 'sr-status-badge--' + normalizedStatus]">
            <span class="sr-status-badge__dot"></span>
            {{ displayStatus }}
          </span>
          <button class="sr-back-btn" @click="goBack">
            <v-icon small color="rgba(255,255,255,0.9)" class="mr-1">mdi-arrow-left</v-icon>
            Back to Reports
          </button>
        </div>
      </div>

      <div class="sr-hero__meta">
        <div class="sr-meta-pill">
          <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-snake</v-icon>
          <span style="text-transform:capitalize">{{ snakeName }}</span>
        </div>
        <div class="sr-meta-pill">
          <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-map-marker-outline</v-icon>
          {{ reportedDistrict }}
        </div>
        <div class="sr-meta-pill">
          <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-clock-outline</v-icon>
          {{ reportedDateLabel }}
        </div>
        <div class="sr-meta-pill" v-if="data && data.rescuer && data.rescuer.name">
          <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-account-hard-hat-outline</v-icon>
          {{ data.rescuer.name }}
        </div>
      </div>
    </div>

    <!-- ══════════════ BODY ══════════════ -->
    <div class="sr-body">

      <!-- ── MAIN COLUMN ── -->
      <div class="sr-main">

        <!-- ▸ REPORT DETAILS -->
        <div class="sr-card">
          <div class="sr-card__hdr sr-card__hdr--report">
            <div class="sr-hdr-icon sr-hdr-icon--report">
              <v-icon color="#EA580C" size="16">mdi-clipboard-text-outline</v-icon>
            </div>
            <span class="sr-card__hdr-title">Report Details</span>
          </div>

          <div class="sr-fields">
            <div class="sr-field" v-if="data && data.reportId && data.reportId.description">
              <div class="sr-field__lbl">Description</div>
              <div class="sr-field__val">{{ data.reportId.description }}</div>
            </div>
            <div class="sr-field" v-if="data && data.reportId && data.reportId.snake">
              <div class="sr-field__lbl">Snake</div>
              <div class="sr-field__val" style="text-transform:capitalize">{{ data.reportId.snake.name }}</div>
            </div>
            <div class="sr-field" v-if="data && data.reportId && data.reportId.createdAt">
              <div class="sr-field__lbl">Reported At</div>
              <div class="sr-field__val">{{ formatDate(data.reportId.createdAt) }}</div>
            </div>
            <div class="sr-field" v-if="data && data.reportId && data.reportId.user">
              <div class="sr-field__lbl">Reported By</div>
              <div class="sr-field__val">
                {{ data.reportId.user.name }}
                <div style="font-size:12px;color:#94A3B8;font-weight:500;margin-top:2px;">{{ data.reportId.user.phone }}</div>
              </div>
            </div>
            <div class="sr-field" v-if="data && data.reportId && data.reportId.situation">
              <div class="sr-field__lbl">Situation</div>
              <div class="sr-field__val">{{ data.reportId.situation }}</div>
            </div>
            <div class="sr-field" v-if="data && divisionname">
              <div class="sr-field__lbl">Division</div>
              <div class="sr-field__val">{{ divisionname }}</div>
            </div>
          </div>

          <div class="sr-photos" v-if="reportedimages && reportedimages.length > 0">
            <div class="sr-photos__label">
              <v-icon size="12" color="#94A3B8" class="mr-1">mdi-image-multiple-outline</v-icon>
              Reported Photographs
            </div>
            <div class="sr-photos__grid">
              <div class="sr-photo" v-for="(item, i) in reportedimages" :key="'rep-'+i" @click="openImagePreview(mediaURL + item)">
                <img :src="mediaURL + item" alt="Photo" />
                <div class="sr-photo__overlay"><v-icon color="white" size="18">mdi-magnify-plus-outline</v-icon></div>
              </div>
            </div>
          </div>
        </div>

        <!-- ▸ RESPONSE TIMELINE -->
        <div class="sr-card" v-if="isRescued">
          <div class="sr-card__hdr">
            <div class="sr-hdr-icon" style="background:rgba(2,132,199,0.1)">
              <v-icon color="#0284C7" size="16">mdi-timeline-clock-outline</v-icon>
            </div>
            <span class="sr-card__hdr-title">Response Timeline</span>
            <div class="sr-total-badge" v-if="totalResponseDuration">
              <v-icon x-small color="#15803D" class="mr-1">mdi-clock-check-outline</v-icon>
              Total: <strong class="ml-1">{{ totalResponseDuration }}</strong>
            </div>
          </div>

          <div class="sr-timeline">
            <div class="sr-tl-step">
              <div class="sr-tl-dot sr-tl-dot--reported">
                <v-icon size="16" color="white">mdi-alert-circle-outline</v-icon>
              </div>
              <div class="sr-tl-info">
                <div class="sr-tl-lbl">Reported</div>
                <div class="sr-tl-time">{{ formatDate(reportedTime) }}</div>
              </div>
            </div>

            <div class="sr-tl-connector" v-if="reportToRescueDuration">
              <div class="sr-tl-line"></div>
              <div class="sr-tl-dur">{{ reportToRescueDuration }}</div>
            </div>

            <div class="sr-tl-step" v-if="isRescued">
              <div class="sr-tl-dot sr-tl-dot--rescued">
                <v-icon size="16" color="white">mdi-lifebuoy</v-icon>
              </div>
              <div class="sr-tl-info">
                <div class="sr-tl-lbl">Rescued</div>
                <div class="sr-tl-time">{{ formatDate(data.rescue.rescuedAt) }}</div>
              </div>
            </div>

            <div class="sr-tl-connector" v-if="rescueToReleaseDuration">
              <div class="sr-tl-line"></div>
              <div class="sr-tl-dur">{{ rescueToReleaseDuration }}</div>
            </div>

            <div class="sr-tl-step" v-if="isReleased">
              <div class="sr-tl-dot sr-tl-dot--released">
                <v-icon size="16" color="white">mdi-forest</v-icon>
              </div>
              <div class="sr-tl-info">
                <div class="sr-tl-lbl">Released</div>
                <div class="sr-tl-time">{{ formatDate(data.release.releasedAt) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ▸ RESCUE DETAILS -->
        <div class="sr-card" v-if="isRescued">
          <div class="sr-card__hdr sr-card__hdr--rescue">
            <div class="sr-hdr-icon sr-hdr-icon--rescue">
              <v-icon color="#16A34A" size="16">mdi-lifebuoy</v-icon>
            </div>
            <span class="sr-card__hdr-title">Rescue Details</span>
          </div>

          <div class="sr-escaped" v-if="isSnakeEscaped">
            <div class="sr-escaped__icon">
              <v-icon color="#B45309" size="20">mdi-run-fast</v-icon>
            </div>
            <div>
              <div class="sr-escaped__title">Snake Escaped</div>
              <div class="sr-escaped__desc">The rescuer attended the site but the snake escaped before it could be captured. No rescue data was recorded.</div>
            </div>
          </div>

          <!-- Rescue fields -->
          <div class="sr-fields" v-if="!isSnakeEscaped || (isSnakeEscaped && data && data.rescuer && data.rescuer.name)">
            <div class="sr-field" v-if="data && data.rescuer && data.rescuer.name">
              <div class="sr-field__lbl">Rescuer</div>
              <div class="sr-field__val">
                {{ data.rescuer.name }}
                <div style="font-size:12px;color:#94A3B8;font-weight:500;margin-top:2px;">{{ data.rescuer.phone }}</div>
              </div>
            </div>
            <template v-if="!isSnakeEscaped">
              <div class="sr-field" v-if="data && data.rescue && data.rescue.rescuedAt">
                <div class="sr-field__lbl">Rescued At</div>
                <div class="sr-field__val">{{ formatDate(data.rescue.rescuedAt) }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.locality">
                <div class="sr-field__lbl">Rescued From</div>
                <div class="sr-field__val">{{ data.rescue.locality }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.district">
                <div class="sr-field__lbl">District</div>
                <div class="sr-field__val">{{ data.rescue.district }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.quantity">
                <div class="sr-field__lbl">Quantity</div>
                <div class="sr-field__val">{{ data.rescue.quantity }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.sex">
                <div class="sr-field__lbl">Sex</div>
                <div class="sr-field__val">{{ data.rescue.sex }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue">
                <div class="sr-field__lbl">Laid Eggs</div>
                <div class="sr-field__val">
                  <span v-if="data.rescue.laidEggs === true">Yes</span>
                  <span v-else-if="data.rescue.laidEggs === false">No</span>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="sr-field" v-if="data && data.rescue">
                <div class="sr-field__lbl">Gravid</div>
                <div class="sr-field__val">
                  <span v-if="data.rescue.gravid === true">Yes</span>
                  <span v-else-if="data.rescue.gravid === false">No</span>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.difficulties">
                <div class="sr-field__lbl">Difficulties</div>
                <div class="sr-field__val">{{ data.rescue.difficulties }}</div>
              </div>
              <div class="sr-field" v-if="data && data.rescue && data.rescue.delayReasons">
                <div class="sr-field__lbl">Delay Reasons</div>
                <div class="sr-field__val">{{ data.rescue.delayReasons }}</div>
              </div>
            </template>
          </div>

          <div class="sr-photos" v-if="rescuedimages && rescuedimages.length > 0 && !isSnakeEscaped">
            <div class="sr-photos__label">
              <v-icon size="12" color="#94A3B8" class="mr-1">mdi-image-multiple-outline</v-icon>
              Rescued Photographs
            </div>
            <div class="sr-photos__grid">
              <div class="sr-photo" v-for="(item, i) in rescuedimages" :key="'rsc-'+i" @click="openImagePreview(mediaURL + item)">
                <img :src="mediaURL + item" alt="Photo" />
                <div class="sr-photo__overlay"><v-icon color="white" size="18">mdi-magnify-plus-outline</v-icon></div>
              </div>
            </div>
          </div>
        </div>

        <!-- ▸ RELEASE / HANDOVER DETAILS -->
        <div class="sr-card" v-if="isReleased">
          <div class="sr-card__hdr sr-card__hdr--release">
            <div class="sr-hdr-icon sr-hdr-icon--release">
              <v-icon color="#7C3AED" size="16">mdi-forest</v-icon>
            </div>
            <span class="sr-card__hdr-title">
              {{ (data && data.release && data.release.type === 'Handover') ? 'Handover Details' : 'Release Details' }}
            </span>
          </div>

          <div class="sr-fields">
            <div class="sr-field" v-if="data && data.release && data.release.releasedAt">
              <div class="sr-field__lbl">{{ (data && data.release && data.release.type === 'Handover') ? 'Handover Date' : 'Released Date' }}</div>
              <div class="sr-field__val">{{ formatDate(data.release.releasedAt) }}</div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.type !== 'Handover'">
              <div class="sr-field__lbl">Released?</div>
              <div class="sr-field__val">
                <span v-if="data.release.releasedOrNot == true">Yes</span>
                <span v-else>No</span>
              </div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.type === 'Handover'">
              <div class="sr-field__lbl">Handover Platform</div>
              <div class="sr-field__val">{{ data.release.handoverplatform || '-' }}</div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.type === 'Handover' && data.release.handoverplatform">
              <div class="sr-field__lbl">Handover Destination</div>
              <div class="sr-field__val">
                <span v-if="data.release.handoverplatform === 'Division' && data.release.handoverdivision">{{ data.release.handoverdivision.divisionName }}</span>
                <span v-else-if="data.release.handoverplatform === 'Range' && data.release.handoverrange">{{ data.release.handoverrange.rangeName }}</span>
                <span v-else-if="data.release.handoverplatform === 'RRT' && data.release.handoverrrt">{{ data.release.handoverrrt.name }}</span>
                <span v-else-if="data.release.handoverplatform === 'SIP' && data.release.handoversip">{{ data.release.handoversip.circleName }}</span>
                <span v-else>-</span>
              </div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.locality">
              <div class="sr-field__lbl">{{ (data && data.release && data.release.type === 'Handover') ? 'Handover Locality' : 'Released From' }}</div>
              <div class="sr-field__val">{{ data.release.locality }}</div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.district">
              <div class="sr-field__lbl">District</div>
              <div class="sr-field__val">{{ data.release.district }}</div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.snakeHealth">
              <div class="sr-field__lbl">Snake Health</div>
              <div class="sr-field__val">{{ data.release.snakeHealth }}</div>
            </div>
            <div class="sr-field" v-if="data && data.release && data.release.type !== 'Handover'">
              <div class="sr-field__lbl">Mishaps</div>
              <div class="sr-field__val">
                <span v-if="data.release.mishaps == true">No Changes</span>
                <span v-else>Yes</span>
              </div>
            </div>
          </div>

          <div class="sr-photos" v-if="releasedimages && releasedimages.length > 0">
            <div class="sr-photos__label">
              <v-icon size="12" color="#94A3B8" class="mr-1">mdi-image-multiple-outline</v-icon>
              Released Photographs
            </div>
            <div class="sr-photos__grid">
              <div class="sr-photo" v-for="(item, i) in releasedimages" :key="'rel-'+i" @click="openImagePreview(mediaURL + item)">
                <img :src="mediaURL + item" alt="Photo" />
                <div class="sr-photo__overlay"><v-icon color="white" size="18">mdi-magnify-plus-outline</v-icon></div>
              </div>
            </div>
          </div>
        </div>

      </div><!-- /sr-main -->

      <!-- ── SIDEBAR ── -->
      <div class="sr-sidebar">

        <div class="sr-card sr-card--map">
          <div class="sr-card__hdr">
            <div class="sr-hdr-icon" style="background:rgba(3,105,161,0.1)">
              <v-icon color="#0369A1" size="16">mdi-map-marker-multiple-outline</v-icon>
            </div>
            <span class="sr-card__hdr-title">Location Map</span>
          </div>
          <div id="map"></div>
          <div class="sr-map-legend">
            <div class="sr-legend-item">
              <span class="sr-legend-dot" style="background:#E65100"></span>Reported
            </div>
            <div class="sr-legend-item" v-if="isRescued && !isSnakeEscaped">
              <span class="sr-legend-dot" style="background:#15803D"></span>Rescued
            </div>
            <div class="sr-legend-item" v-if="isReleased">
              <span class="sr-legend-dot" style="background:#6B21A8"></span>Released
            </div>
          </div>
        </div>

        <!-- Admin Actions -->
        <div class="sr-card">
          <div class="sr-card__hdr">
            <div class="sr-hdr-icon" style="background:rgba(220,38,38,0.08)">
              <v-icon color="#DC2626" size="16">mdi-shield-alert-outline</v-icon>
            </div>
            <span class="sr-card__hdr-title">Admin Actions</span>
          </div>
          <div style="padding:16px 20px; display:flex; flex-direction:column; gap:10px;">
            <v-btn
              v-if="data && data.status === 'Released'"
              block depressed
              color="#0D9488"
              @click="approveoperationDialog = true"
              style="border-radius:10px;text-transform:none;font-weight:600;letter-spacing:0;color:#fff;"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>Approve Operation
            </v-btn>
            <v-btn
              v-if="data && data.status !== 'Closed'"
              block depressed
              color="#EAB308"
              @click="approveDialog = true"
              style="border-radius:10px;text-transform:none;font-weight:600;letter-spacing:0;color:#fff;"
            >
              <v-icon left small>mdi-lock-check-outline</v-icon>Close Operation
            </v-btn>
            <v-btn block depressed color="error" @click="rejectDialog = true" style="border-radius:10px;text-transform:none;font-weight:600;letter-spacing:0;">
              <v-icon left small>mdi-delete-outline</v-icon>Delete Report
            </v-btn>
          </div>
        </div>

      </div><!-- /sr-sidebar -->

    </div><!-- /sr-body -->

    <!-- ══════════════ DIALOGS ══════════════ -->
    <v-dialog v-model="approveDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Close Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <span class="subheading">Remarks</span>
              <v-text-field outlined dense v-model="closeremarks"></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" :ripple="false" depressed @click="approveDialog = false" class="itemValue">Cancel</v-btn>
          <v-btn tile :color="appColor" :ripple="false" depressed @click="closeoperation()" class="itemValue"><span style="color:#fff">Close</span></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveoperationDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Approve Operation</v-card-title>
        <v-card-text>
          <v-layout wrap justify-center>
            <v-flex xs12>
              <span class="subheading">Remarks</span>
              <v-text-field outlined dense v-model="approveremarks"></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" :ripple="false" depressed @click="approveoperationDialog = false" class="itemValue">Cancel</v-btn>
          <v-btn tile :color="appColor" :ripple="false" depressed @click="approveoperation()" class="itemValue"><span style="color:#fff">Approve</span></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="500">
      <v-card>
        <v-card-title class="heading">Confirmation</v-card-title>
        <v-card-text>Are you sure you want to delete the operation?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn tile outlined color="#FF1313" :ripple="false" depressed @click="rejectDialog = false" class="itemValue">Cancel</v-btn>
          <v-btn tile :color="appColor" :ripple="false" depressed @click="deleteoperation()" class="itemValue"><span style="color:#fff">Submit</span></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image Preview -->
    <v-dialog v-model="previewDialog" max-width="860px" overlay-color="#0A1A0F" overlay-opacity="0.88">
      <v-card flat style="border-radius:18px;overflow:hidden;background:#fff;">
        <v-card-title class="d-flex justify-space-between align-center" style="padding:16px 20px;border-bottom:1px solid rgba(0,0,0,0.06);">
          <span style="font-size:14px;font-weight:700;color:#0F172A;">Image Preview</span>
          
          <!-- Zoom Controls -->
          <div class="d-flex align-center" style="gap: 8px;">
            <v-btn icon small @click="zoomOut" :disabled="zoomScale <= 0.5" title="Zoom Out">
              <v-icon color="#64748B" small>mdi-magnify-minus</v-icon>
            </v-btn>
            <span style="font-size: 12px; font-weight: 600; color: #475569; min-width: 45px; text-align: center;">
              {{ Math.round(zoomScale * 100) }}%
            </span>
            <v-btn icon small @click="zoomIn" :disabled="zoomScale >= 4.0" title="Zoom In">
              <v-icon color="#64748B" small>mdi-magnify-plus</v-icon>
            </v-btn>
            <v-btn icon small @click="resetZoom" :disabled="zoomScale === 1" title="Reset Zoom">
              <v-icon color="#64748B" small>mdi-aspect-ratio</v-icon>
            </v-btn>
          </div>

          <v-btn icon small @click="previewDialog = false">
            <v-icon color="#64748B" small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <div 
          @mousedown="startPan" 
          @mousemove="pan" 
          @mouseup="endPan" 
          @mouseleave="endPan"
          @touchstart="startPanTouch"
          @touchmove="panTouch"
          @touchend="endPan"
          :style="{ cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }"
          style="padding:16px;background:#F8FAFC;display:flex;justify-content:center;align-items:center;min-height:350px;max-height:72vh;overflow:hidden;position:relative;user-select:none;"
        >
          <img 
            :src="previewImageUrl" 
            :style="{ 
              transform: 'translate(' + panX + 'px, ' + panY + 'px) scale(' + zoomScale + ')', 
              transition: isDragging ? 'none' : 'transform 0.15s ease'
            }" 
            style="max-width:100%;max-height:60vh;object-fit:contain;border-radius:10px;display:block;transform-origin:center center;pointer-events:none;" 
          />
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<script>
var marker;
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      data: [],
      reportedimages: [],
      rescuedimages:[],
      releasedimages:[],
      map: "",
      mapData: {},
      location: [],
      approveDialog: false,
      closeremarks: "",
      rejectDialog: false,
      divisionname: "",
      photoArray: [],
      approveremarks:"",
      idByAdmin: "",
      distance: "",
      approveoperationDialog: false,
      previewDialog: false,
      previewImageUrl: "",
      zoomScale: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
    };
  },
  computed: {
    displayStatus() {
      return this.data && this.data.status ? this.data.status : "Pending";
    },
    normalizedStatus() {
      return String(this.displayStatus).toLowerCase().replace(/[\s_]+/g, "");
    },
    snakeName() {
      return this.data &&
        this.data.reportId &&
        this.data.reportId.snake &&
        this.data.reportId.snake.name
        ? this.data.reportId.snake.name
        : "-";
    },
    reportedDistrict() {
      if (this.data && this.data.reportId && this.data.reportId.district) {
        return this.data.reportId.district;
      }
      if (this.data && this.data.rescue && this.data.rescue.district) {
        return this.data.rescue.district;
      }
      return "-";
    },
    reportedDateLabel() {
      if (this.data && this.data.reportedAt) {
        return this.formatDate(this.data.reportedAt);
      }
      if (this.data && this.data.reportId && this.data.reportId.createdAt) {
        return this.formatDate(this.data.reportId.createdAt);
      }
      return "-";
    },
    listPath() {
      const path = this.$route.path || "";
      const isOfficer = path.startsWith("/officers/");
      const prefix = isOfficer ? "/officers" : "";
      if (path.includes("assignedoperations")) return `${prefix}/assignedoperations`;
      if (path.includes("rescuedoperations")) return `${prefix}/rescuedoperations`;
      if (path.includes("releasedoperations")) return `${prefix}/releasedoperations`;
      if (path.includes("approvedoperations")) return `${prefix}/approvedoperations`;
      if (path.includes("deleteoperations")) return `${prefix}/deleteoperations`;
      if (path.includes("fraudcases") || path.includes("fraudoperations")) return `${prefix}/fraudoperations`;
      if (path.includes("traininglist")) return `${prefix}/traininglist`;
      return isOfficer ? "/officers/snakereports" : "/snakereports";
    },
    showDeleteAction() {
      return !(this.$route.path && this.$route.path.startsWith("/officers/"));
    },
    isSnakeEscaped() {
      return !!(this.data && this.data.approval && this.data.approval.remarks === 'Snake escaped');
    },
    isRescued() {
      if (!this.data || !this.data.status) return false;
      const activeRescueStatuses = ['Rescued', 'Released', 'Approved', 'OnReview', 'RequestedDeletion'];
      if (activeRescueStatuses.includes(this.data.status)) return true;
      if (this.data.status === 'Closed') {
        if (this.isSnakeEscaped) return true;
        return !!(this.data.rescuer && this.data.rescuer.name) ||
               !!(this.data.rescue && (this.data.rescue.locality || this.data.rescue.snake || this.data.rescue.quantity)) ||
               !!(this.rescuedimages && this.rescuedimages.length > 0);
      }
      return false;
    },
    isReleased() {
      if (!this.data || !this.data.status) return false;
      const activeReleaseStatuses = ['Released', 'Approved'];
      if (activeReleaseStatuses.includes(this.data.status)) return true;
      if (this.data.status === 'Closed') {
        return !!(this.data.release && (this.data.release.releasedOrNot === true || this.data.release.locality || this.data.release.handoverplatform)) ||
               !!(this.releasedimages && this.releasedimages.length > 0);
      }
      return false;
    },
    reportedTime() {
      if (this.data && this.data.reportedAt) return this.data.reportedAt;
      if (this.data && this.data.reportId && this.data.reportId.createdAt) return this.data.reportId.createdAt;
      return null;
    },
    reportToRescueDuration() {
      if (this.isRescued && this.reportedTime && this.data.rescue && this.data.rescue.rescuedAt) {
        return this.getDurationLabel(this.reportedTime, this.data.rescue.rescuedAt);
      }
      return null;
    },
    rescueToReleaseDuration() {
      if (this.isReleased && this.data.rescue && this.data.rescue.rescuedAt && this.data.release && this.data.release.releasedAt) {
        return this.getDurationLabel(this.data.rescue.rescuedAt, this.data.release.releasedAt);
      }
      return null;
    },
    totalResponseDuration() {
      if (this.isReleased && this.reportedTime && this.data.release && this.data.release.releasedAt) {
        return this.getDurationLabel(this.reportedTime, this.data.release.releasedAt);
      }
      return null;
    },
  },
  beforeMount() {
    this.getData();
  },
  methods: {
    initMap() {
      var vm = this;
      var reportedLat = null;
      var reportedLon = null;
      if (vm.location && vm.location.length === 2) {
        reportedLat = vm.location[1];
        reportedLon = vm.location[0];
      }
      var centerLat = reportedLat || 10.8505;
      var centerLon = reportedLon || 76.2711;
      var zoomLevel = reportedLat ? 12 : 8;
      if (typeof L === 'undefined') { console.error('Leaflet not loaded.'); return; }
      if (vm.map && vm.map.remove) vm.map.remove();
      vm.map = L.map('map').setView([centerLat, centerLon], zoomLevel);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '© OpenStreetMap contributors'
      }).addTo(vm.map);
      const createCustomIcon = (color, iconClass) => L.divIcon({
        className: 'custom-map-marker',
        html: `<div class="marker-pin" style="background-color:${color};"><i class="v-icon notranslate mdi ${iconClass} theme--light" style="color:white;font-size:14px;"></i></div><div class="marker-pulse" style="background-color:${color};"></div>`,
        iconSize: [30, 42], iconAnchor: [15, 42], popupAnchor: [0, -36]
      });
      const reportedIcon = createCustomIcon('#E65100', 'mdi-alert-circle');
      const rescuedIcon  = createCustomIcon('#1B5E20', 'mdi-lifebuoy');
      const releasedIcon = createCustomIcon('#6B21A8', 'mdi-forest');
      var markersGroup = [];
      if (reportedLat && reportedLon) {
        L.marker([reportedLat, reportedLon], { icon: reportedIcon }).addTo(vm.map)
          .bindPopup(`<div style="font-family:sans-serif;padding:4px;"><h4 style="margin:0 0 6px 0;color:#E65100;">Reported Location</h4><p style="margin:0;font-size:12px;color:#555;">Coordinates: ${reportedLat.toFixed(5)}, ${reportedLon.toFixed(5)}</p></div>`);
        markersGroup.push([reportedLat, reportedLon]);
      }
      if (vm.isRescued && vm.data.rescue && vm.data.rescue.location && vm.data.rescue.location.length === 2) {
        var rescueLat = vm.data.rescue.location[1];
        var rescueLon = vm.data.rescue.location[0];
        L.marker([rescueLat, rescueLon], { icon: rescuedIcon }).addTo(vm.map)
          .bindPopup(`<div style="font-family:sans-serif;padding:4px;"><h4 style="margin:0 0 6px 0;color:#1B5E20;">Rescued Location</h4><p style="margin:0;font-size:12px;color:#555;">Coordinates: ${rescueLat.toFixed(5)}, ${rescueLon.toFixed(5)}</p></div>`);
        markersGroup.push([rescueLat, rescueLon]);
      }
      if (vm.isReleased && vm.data.release && vm.data.release.location && vm.data.release.location.length === 2) {
        var releaseLat = vm.data.release.location[1];
        var releaseLon = vm.data.release.location[0];
        L.marker([releaseLat, releaseLon], { icon: releasedIcon }).addTo(vm.map)
          .bindPopup(`<div style="font-family:sans-serif;padding:4px;"><h4 style="margin:0 0 6px 0;color:#6B21A8;">Released Location</h4><p style="margin:0;font-size:12px;color:#555;">Coordinates: ${releaseLat.toFixed(5)}, ${releaseLon.toFixed(5)}</p></div>`);
        markersGroup.push([releaseLat, releaseLon]);
      }
      if (markersGroup.length > 1) vm.map.fitBounds(markersGroup, { padding: [50, 50] });
    },
    goBack() {
      this.$router.push({ path: this.listPath }).catch(() => {});
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/operation/get",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { id: this.$route.query.id },
      }).then((response) => {
        if (response.data.status == true) {
          this.data = response.data.data;
          if (this.data && this.data.division && this.data.division.length > 0) {
            this.divisionname = this.data.division.map(d => typeof d === 'object' && d.divisionName ? d.divisionName : d).join(', ');
          }
          this.reportedimages = response.data.reportedimages;
          this.rescuedimages = response.data.rescuedimages;
          this.releasedimages = response.data.releasedimages;
          this.location = this.data && this.data.reportId && this.data.reportId.location ? this.data.reportId.location : [];
          this.photoArray = Array.isArray(this.data.photographs) ? this.data.photographs.map((p) => p.photo) : [];
          this.appLoading = false;
          this.$nextTick(() => { this.initMap(); });
        } else {
          this.msg = response.data.msg;
          this.showSnackBar = true;
          this.appLoading = false;
        }
      }).catch((err) => {
        this.appLoading = false;
        this.ServerError = true;
        console.log(err);
      });
    },
    closeoperation() {
      if (!this.closeremarks) { this.msg = "Please enter remarks."; this.showSnackBar = true; return; }
      this.appLoading = true;
      axios({ method: "POST", url: "/operation/closereport", headers: { "x-auth-token": localStorage.getItem("token") }, data: { id: this.$route.query.id, remarks: this.closeremarks } })
        .then((response) => {
          this.msg = response.data.status ? "Report Closed Successfully." : response.data.msg;
          this.showSnackBar = true;
          this.appLoading = false;
        }).catch((err) => { this.appLoading = false; this.ServerError = true; console.log(err); });
    },
    approveoperation() {
      if (!this.approveremarks) { this.msg = "Please enter remarks."; this.showSnackBar = true; return; }
      this.appLoading = true;
      axios({ method: "POST", url: "/operation/approve", headers: { "x-auth-token": localStorage.getItem("token") }, data: { id: this.$route.query.id, remarks: this.approveremarks, idByAdmin: this.idByAdmin, distance: this.distance } })
        .then((response) => {
          this.msg = response.data.status ? "Approved Successfully." : response.data.msg;
          this.showSnackBar = true;
          this.appLoading = false;
        }).catch((err) => { this.appLoading = false; this.ServerError = true; console.log(err); });
    },
    deleteoperation() {
      this.appLoading = true;
      axios({ method: "POST", url: "/operation/delete", headers: { "x-auth-token": localStorage.getItem("token") }, data: { id: this.$route.query.id } })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Operation Deleted Successfully.";
            this.showSnackBar = true;
            this.appLoading = false;
            this.rejectDialog = false;
            this.$router.push({ path: this.listPath });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        }).catch((err) => { this.appLoading = false; this.ServerError = true; console.log(err); });
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d.getTime())) {
        try { const [year, month, day] = date.slice(0, 10).split('-'); return `${day}-${month}-${year}`; } catch(e) { return '-'; }
      }
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12; hours = hours ? hours : 12;
      return `${day}-${month}-${year} ${String(hours).padStart(2,'0')}:${minutes} ${ampm}`;
    },
    getDurationLabel(start, end) {
      if (!start || !end) return null;
      const diffMs = new Date(end) - new Date(start);
      if (diffMs < 0 || isNaN(diffMs)) return null;
      const diffMins = Math.floor(diffMs / 60000);
      const days = Math.floor(diffMins / 1440);
      const hours = Math.floor((diffMins % 1440) / 60);
      const mins = diffMins % 60;
      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
      return parts.join(' ');
    },
    openImagePreview(url) {
      this.previewImageUrl = url;
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
      this.previewDialog = true;
    },
    zoomIn() {
      if (this.zoomScale < 4.0) {
        this.zoomScale = parseFloat((this.zoomScale + 0.25).toFixed(2));
      }
    },
    zoomOut() {
      if (this.zoomScale > 0.5) {
        this.zoomScale = parseFloat((this.zoomScale - 0.25).toFixed(2));
        if (this.zoomScale <= 1) {
          this.panX = 0;
          this.panY = 0;
        }
      }
    },
    resetZoom() {
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
    },
    startPan(e) {
      if (this.zoomScale <= 1) return;
      this.isDragging = true;
      this.startX = e.clientX - this.panX;
      this.startY = e.clientY - this.panY;
    },
    pan(e) {
      if (!this.isDragging) return;
      this.panX = e.clientX - this.startX;
      this.panY = e.clientY - this.startY;
    },
    startPanTouch(e) {
      if (this.zoomScale <= 1) return;
      if (e.touches.length === 1) {
        this.isDragging = true;
        this.startX = e.touches[0].clientX - this.panX;
        this.startY = e.touches[0].clientY - this.panY;
      }
    },
    panTouch(e) {
      if (!this.isDragging) return;
      if (e.touches.length === 1) {
        this.panX = e.touches[0].clientX - this.startX;
        this.panY = e.touches[0].clientY - this.startY;
      }
    },
    endPan() {
      this.isDragging = false;
    }
  },
};
</script>
<style>
.sr-wrapper {
  background: #EEF3EC;
  min-height: 100vh;
  font-family: 'Inter', 'poppinsregular', sans-serif;
}
.sr-hero {
  background: linear-gradient(135deg, #0A2318 0%, #14532D 55%, #166534 100%);
  padding: 28px 32px 24px;
}
.sr-hero__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}
.sr-hero__eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.sr-hero__title {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}
.sr-hero__accent { color: #4ADE80; }
.sr-hero__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;
}
.sr-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sr-status-badge__dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.sr-status-badge--pending    { background: rgba(249,115,22,0.18); color: #FED7AA; border: 1px solid rgba(249,115,22,0.35); }
.sr-status-badge--assigned   { background: rgba(59,130,246,0.18); color: #BFDBFE; border: 1px solid rgba(59,130,246,0.35); }
.sr-status-badge--rescued    { background: rgba(34,197,94,0.18);  color: #BBF7D0; border: 1px solid rgba(34,197,94,0.35); }
.sr-status-badge--released   { background: rgba(167,139,250,0.18);color: #DDD6FE; border: 1px solid rgba(167,139,250,0.35); }
.sr-status-badge--approved   { background: rgba(20,184,166,0.18); color: #99F6E4; border: 1px solid rgba(20,184,166,0.35); }
.sr-status-badge--closed     { background: rgba(148,163,184,0.18);color: #CBD5E1; border: 1px solid rgba(148,163,184,0.35); }
.sr-status-badge--fraud,
.sr-status-badge--fraudcase  { background: rgba(239,68,68,0.18);  color: #FECACA; border: 1px solid rgba(239,68,68,0.35); }
.sr-back-btn {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 10px;
  padding: 8px 16px;
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.sr-back-btn:hover { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.3); }
.sr-hero__meta { display: flex; flex-wrap: wrap; gap: 8px; }
.sr-meta-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.8);
}
.sr-body { display: flex; gap: 20px; padding: 24px; align-items: flex-start; }
.sr-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 16px; }
.sr-sidebar { width: 340px; flex-shrink: 0; display: flex; flex-direction: column; gap: 16px; position: sticky; top: 20px; }
.sr-card { background: #fff; border-radius: 18px; border: 1px solid rgba(15,23,42,0.07); box-shadow: 0 2px 16px rgba(15,23,42,0.04); overflow: hidden; }
.sr-card__hdr { display: flex; align-items: center; gap: 10px; padding: 14px 20px; background: #FAFBFA; border-bottom: 1px solid rgba(15,23,42,0.06); }
.sr-card__hdr--report  { border-left: 4px solid #F97316; }
.sr-card__hdr--rescue  { border-left: 4px solid #16A34A; }
.sr-card__hdr--release { border-left: 4px solid #7C3AED; }
.sr-hdr-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: rgba(15,23,42,0.06); }
.sr-hdr-icon--report  { background: rgba(249,115,22,0.1); }
.sr-hdr-icon--rescue  { background: rgba(22,163,74,0.1); }
.sr-hdr-icon--release { background: rgba(124,58,237,0.1); }
.sr-card__hdr-title { font-size: 13px; font-weight: 700; color: #0F172A; letter-spacing: 0.01em; flex: 1; }
.sr-total-badge { display: inline-flex; align-items: center; background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 999px; padding: 3px 12px; font-size: 11px; font-weight: 600; color: #15803D; white-space: nowrap; margin-left: auto; }
.sr-fields { display: grid; grid-template-columns: repeat(auto-fill, minmax(175px, 1fr)); gap: 1px; background: rgba(15,23,42,0.05); }
.sr-field { padding: 14px 20px; background: #fff; }
.sr-field__lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #94A3B8; margin-bottom: 5px; }
.sr-field__val { font-size: 13px; font-weight: 600; color: #1E293B; line-height: 1.45; word-break: break-word; }
.sr-photos { padding: 14px 20px 18px; border-top: 1px solid rgba(15,23,42,0.05); }
.sr-photos__label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #94A3B8; margin-bottom: 10px; display: flex; align-items: center; }
.sr-photos__grid { display: flex; flex-wrap: wrap; gap: 8px; }
.sr-photo { position: relative; width: 110px; height: 88px; border-radius: 10px; overflow: hidden; cursor: pointer; border: 1px solid rgba(15,23,42,0.08); flex-shrink: 0; }
.sr-photo img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.25s ease; }
.sr-photo__overlay { position: absolute; inset: 0; background: rgba(0,0,0,0); display: flex; align-items: center; justify-content: center; transition: background 0.2s ease; }
.sr-photo:hover img { transform: scale(1.08); }
.sr-photo:hover .sr-photo__overlay { background: rgba(0,0,0,0.32); }
.sr-timeline { padding: 20px 24px; display: flex; align-items: center; flex-wrap: wrap; gap: 0; overflow-x: auto; }
.sr-tl-step { display: flex; align-items: center; gap: 12px; }
.sr-tl-dot { width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 14px rgba(0,0,0,0.15); }
.sr-tl-dot--reported { background: linear-gradient(135deg, #EA580C, #F97316); }
.sr-tl-dot--rescued  { background: linear-gradient(135deg, #15803D, #16A34A); }
.sr-tl-dot--released { background: linear-gradient(135deg, #5B21B6, #7C3AED); }
.sr-tl-info { display: flex; flex-direction: column; gap: 2px; }
.sr-tl-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #94A3B8; }
.sr-tl-time { font-size: 12px; font-weight: 600; color: #1E293B; white-space: nowrap; }
.sr-tl-connector { flex: 1; min-width: 60px; display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 0 12px; }
.sr-tl-line { height: 2px; width: 100%; background: repeating-linear-gradient(90deg, #CBD5E1, #CBD5E1 4px, transparent 4px, transparent 8px); border-radius: 2px; }
.sr-tl-dur { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 999px; padding: 2px 10px; font-size: 10px; font-weight: 700; color: #15803D; white-space: nowrap; }
.sr-escaped { margin: 16px 20px; padding: 16px 18px; border-radius: 14px; background: linear-gradient(135deg, #FFFBEB, #FEF3C7); border: 1.5px solid #F59E0B; border-left: 5px solid #D97706; display: flex; gap: 14px; align-items: flex-start; }
.sr-escaped__icon { width: 40px; height: 40px; border-radius: 50%; background: rgba(217,119,6,0.12); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sr-escaped__title { font-size: 14px; font-weight: 700; color: #92400E; margin-bottom: 4px; }
.sr-escaped__desc { font-size: 12px; color: #B45309; line-height: 1.55; }
.sr-card--map #map { height: 290px; width: 100%; }
.sr-map-legend { display: flex; flex-wrap: wrap; gap: 12px; padding: 12px 20px; border-top: 1px solid rgba(15,23,42,0.06); }
.sr-legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #64748B; font-weight: 600; }
.sr-legend-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.custom-map-marker { position: relative; width: 30px !important; height: 42px !important; }
.marker-pin { width: 30px; height: 30px; border-radius: 50% 50% 50% 0; position: absolute; transform: rotate(-45deg); left: 50%; top: 50%; margin: -15px 0 0 -15px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.25); border: 2px solid white; z-index: 10; }
.marker-pin i { transform: rotate(45deg); display: block; margin: 0; }
.marker-pulse { background: rgba(0,0,0,0.2); border-radius: 50%; height: 14px; width: 14px; position: absolute; left: 50%; top: 50%; margin: 11px 0 0 -7px; transform: rotateX(55deg); z-index: 9; }
.marker-pulse::after { content: ""; border-radius: 50%; height: 40px; width: 40px; position: absolute; margin: -13px 0 0 -13px; animation: pulsate 1.8s ease-out infinite; opacity: 0; box-shadow: 0 0 1px 2px inherit; }
@keyframes pulsate { 0% { transform: scale(0.1, 0.1); opacity: 0; } 50% { opacity: 0.8; } 100% { transform: scale(1.2, 1.2); opacity: 0; } }
.v-dialog__content { z-index: 99999 !important; }
.v-overlay { z-index: 99998 !important; }
@media (max-width: 960px) {
  .sr-body { flex-direction: column; padding: 16px; }
  .sr-sidebar { width: 100%; position: static; }
  .sr-hero { padding: 20px; }
  .sr-hero__top { flex-direction: column; gap: 14px; }
  .sr-hero__right { align-items: flex-start; flex-direction: row; flex-wrap: wrap; }
  .sr-fields { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 480px) {
  .sr-fields { grid-template-columns: 1fr; }
  .sr-hero__title { font-size: 20px; }
}
.heading { font-family: poppinsregular; font-size: 18px; font-weight: 700; }
.subheading { font-family: poppinsregular; font-size: 14px; }
.itemKey { font-size: 12px; color: #64748B; }
.itemValue { font-size: 13px; color: #1E293B; }
</style>
